import Collapse from "@kunukn/react-collapse";
import React, { memo, ReactNode, useCallback } from "react";
import styled, { css } from "styled-components";
import { Icon } from "../Icon";
import { UnstyledButton } from "../UnstyledButton";
import { style } from "../style";
import { focusOutline } from "../utils/focusOutlineStyle";

type TitleTextProps = {
  withIcon?: boolean;
};

const TitleText = styled.span<TitleTextProps>`
  color: ${style.colors.grey.darkest};
  line-height: ${style.vars.space["5"]};

  ${({ withIcon }) =>
    withIcon &&
    css`
      padding-left: ${style.vars.space["3"]};
    `};
`;

const IconContainer = styled.div`
  background-color: ${style.colors.grey.light};
  color: ${style.colors.grey.dark};
  border: none;
  border-radius: ${style.vars.borderRadius.base};
  cursor: pointer;
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${style.vars.space["3"]};
  height: ${style.vars.space["3"]};
  padding: 2px;
`;

const ItemContainer = styled.div<{ disabled: boolean; isExpanded: boolean }>`
  background-color: ${style.colors.white};
  display: flex;
  flex-direction: column;
  box-sizing: content-box;
  min-height: ${style.vars.space["9"]};
  overflow: hidden;

  &:not(:last-of-type) {
    border-bottom: ${style.vars.borderWidth.thin} solid ${style.colors.grey.primary};
  }

  &:not([disabled]) {
    &:hover {
      ${TitleText}, ${IconContainer} {
        color: ${style.colors.blue.dark};
      }
    }
  }

  .collapse-css-transition {
    overflow-y: auto;
  }
`;

const ItemHeader = styled(UnstyledButton)`
  box-sizing: border-box;
  border: none;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding: ${style.vars.space["2"]} ${style.vars.space["2"]};
  margin: ${style.vars.space["2"]} ${style.vars.space["2"]};

  &:active {
    background-color: ${style.colors.blue.lightest};
  }

  &:focus {
    ${focusOutline()};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
`;

const StyledCollapse = styled(Collapse)`
  overflow-y: auto;
  transition: height ${style.vars.transitionSpeed.base} ease-in-out;
`;

const ItemContent = styled.div`
  padding: ${style.vars.space["3"]};
`;

export type AccordionItemProps = {
  id?: string;
  icon?: ReactNode;
  title: string;
  children: any;
  disabled?: boolean;
  /** Do not provide the following props; they're passed in by the Accordion component  */
  parentId?: string;
  index?: number;
  isExpanded?: boolean;
  setExpandedIndex?: (arg0?: number) => void;
};

export const AccordionItem: React.FC<AccordionItemProps> = memo(
  ({ icon, title, children, disabled = false, parentId, index, isExpanded = false, setExpandedIndex }) => {
    const contentId = `${parentId}-item-${index}-content`;
    const handleClick = useCallback(() => setExpandedIndex?.(isExpanded ? undefined : index), [index, isExpanded, setExpandedIndex]);

    return (
      <ItemContainer disabled={disabled} isExpanded={isExpanded}>
        <ItemHeader aria-expanded={isExpanded} aria-controls={contentId} aria-label={title} onClick={handleClick} disabled={disabled}>
          <TitleContainer>
            {icon} <TitleText withIcon={!!icon}>{title}</TitleText>
          </TitleContainer>
          <IconContainer>
            <Icon icon={isExpanded ? "minus" : "plus"} />
          </IconContainer>
        </ItemHeader>
        <StyledCollapse isOpen={isExpanded} overflowOnExpanded>
          <ItemContent id={contentId} role="region" aria-hidden={!isExpanded}>
            {children}
          </ItemContent>
        </StyledCollapse>
      </ItemContainer>
    );
  }
);
